import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Vote, Send, CheckCircle2, BarChart3, Edit, Clock, ListFilter, Search, Check, Loader2 } from 'lucide-react';
import { API } from '@/lib/helpers';

const FILTROS = [
  { key: 'todas', label: 'Todas', icon: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  { key: 'pendentes', label: 'Pendentes', icon: 'bg-amber-100', iconColor: 'text-amber-600' },
  { key: 'respondidas', label: 'Respondidas', icon: 'bg-green-100', iconColor: 'text-green-600' },
];

export default function AuxiliarVotacoesTab() {
  const [votacoes, setVotacoes] = useState([]);
  const [filter, setFilter] = useState('todas');
  const [search, setSearch] = useState('');
  const [selectedVotacao, setSelectedVotacao] = useState(null);
  const [editingResposta, setEditingResposta] = useState(false);
  const [respostaTexto, setRespostaTexto] = useState('');
  const [opcoesSelecionadas, setOpcoesSelecionadas] = useState([]);
  const [resultadoOpen, setResultadoOpen] = useState(null);
  const [resultadoData, setResultadoData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchVotacoes = async () => {
    try {
      const res = await fetch(`${API}/votacoes/auxiliar`, { credentials: 'include' });
      if (res.ok) setVotacoes(await res.json());
    } catch {}
  };

  useEffect(() => { fetchVotacoes(); }, []);

  const counts = {
    todas: votacoes.length,
    pendentes: votacoes.filter(v => !v.minha_resposta && v.status !== 'encerrada').length,
    respondidas: votacoes.filter(v => v.minha_resposta || v.status === 'encerrada').length,
  };

  const filtered = votacoes.filter(v => {
    if (filter === 'pendentes' && (v.minha_resposta || v.status === 'encerrada')) return false;
    if (filter === 'respondidas' && !v.minha_resposta && v.status !== 'encerrada') return false;
    if (search && !v.pergunta.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleResponder = async () => {
    if (!selectedVotacao) return;
    if (selectedVotacao.tipo === 'texto' && !respostaTexto.trim()) {
      toast.error('Por favor, escreva sua resposta.'); return;
    }
    if (selectedVotacao.tipo !== 'texto' && opcoesSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma opção.'); return;
    }
    setSaving(true);
    try {
      const body = selectedVotacao.tipo === 'texto'
        ? { texto: respostaTexto }
        : { opcoes_selecionadas: opcoesSelecionadas };
      const method = editingResposta ? 'PUT' : 'POST';
      const res = await fetch(`${API}/votacoes/${selectedVotacao.votacao_id}/responder`, {
        method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Erro'); }
      toast.success(editingResposta ? 'Resposta atualizada!' : 'Resposta enviada!');
      setSelectedVotacao(null); setRespostaTexto(''); setOpcoesSelecionadas([]); setEditingResposta(false);
      fetchVotacoes();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleOpcao = (idx, permitirMultiplas) => {
    if (permitirMultiplas) {
      setOpcoesSelecionadas(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
    } else {
      setOpcoesSelecionadas([idx]);
    }
  };

  const openVotacao = (v) => {
    setSelectedVotacao(v); setRespostaTexto(''); setOpcoesSelecionadas([]); setEditingResposta(false);
  };

  const openEditResposta = (v) => {
    setSelectedVotacao(v); setEditingResposta(true);
    if (v.minha_resposta?.tipo === 'texto') {
      setRespostaTexto(v.minha_resposta.texto || ''); setOpcoesSelecionadas([]);
    } else {
      setRespostaTexto(''); setOpcoesSelecionadas(v.minha_resposta?.opcoes_selecionadas || []);
    }
  };

  const openResultados = async (v) => {
    setResultadoOpen(v);
    setResultadoData(null);
    try {
      const res = await fetch(`${API}/votacoes/${v.votacao_id}/resultados`, { credentials: 'include' });
      if (res.ok) setResultadoData(await res.json());
      else { const e = await res.json(); toast.error(e.detail || 'Erro ao carregar resultados'); setResultadoOpen(null); }
    } catch { toast.error('Erro ao carregar resultados'); setResultadoOpen(null); }
  };

  // Polling a cada 5s apenas quando o modal de resultados está aberto
  const pollingRef = useRef(null);
  useEffect(() => {
    if (resultadoOpen) {
      pollingRef.current = setInterval(async () => {
        try {
          const res = await fetch(`${API}/votacoes/${resultadoOpen.votacao_id}/resultados`, { credentials: 'include' });
          if (res.ok) setResultadoData(await res.json());
        } catch {}
      }, 5000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [resultadoOpen]);

  const canSeeResults = (v) => {
    if (v.resultado_visibilidade === 'apos_votar' && (v.minha_resposta || v.status === 'encerrada')) return true;
    if (v.resultado_visibilidade === 'apos_encerrar' && v.status === 'encerrada') return true;
    return false;
  };

  const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return dt.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) + ' ' + dt.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Vote className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Votações</h2>
                  <Badge className="bg-indigo-100 text-indigo-700 text-sm font-bold">{counts.todas}</Badge>
                </div>
                <p className="text-sm text-slate-500">Filtre por status</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="aux-votacoes-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`aux-filtro-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.icon}`}>
                      <ListFilter className={`w-4 h-4 ${f.iconColor}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-indigo-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Vote className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'votação' : 'votações'}</p>
              </div>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar votação..." className="pl-10 bg-slate-50 border-slate-200" data-testid="aux-votacoes-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-0" data-testid="aux-votacoes-list">
              {filtered.map(v => (
                <div key={v.votacao_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`aux-votacao-${v.votacao_id}`}>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${v.minha_resposta ? 'bg-green-100' : 'bg-indigo-100'}`}>
                      {v.minha_resposta ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Vote className="w-4 h-4 text-indigo-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-900 font-medium">{v.pergunta}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />{fmtDate(v.enviada_at || v.created_at)}
                        </span>
                        <Badge className={`text-xs ${v.status === 'enviada' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                          {v.status === 'enviada' ? 'Pendente' : 'Respondida'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">{v.tipo === 'texto' ? 'Texto' : 'Múltipla escolha'}</Badge>
                        {v.total_respostas > 0 && <span className="text-[10px] text-slate-400">{v.total_respostas} {v.total_respostas === 1 ? 'resposta' : 'respostas'}</span>}
                      </div>
                      {v.minha_resposta?.tipo === 'texto' && (
                        <p className="text-xs text-slate-500 mt-1 truncate max-w-[300px]">"{v.minha_resposta.texto}"</p>
                      )}
                      {v.minha_resposta?.opcoes_selecionadas && v.opcoes && (
                        <p className="text-xs text-slate-500 mt-1 truncate max-w-[300px]">
                          "{v.minha_resposta.opcoes_selecionadas.map(idx => v.opcoes[idx]?.texto).filter(Boolean).join(', ')}"
                        </p>
                      )}
                    </div>
                    <div className="flex gap-0.5 flex-shrink-0 items-center">
                      {!v.minha_resposta && v.status !== 'encerrada' && (
                        <Button variant="outline" size="sm" className="text-green-600 border-green-600 hover:bg-green-50 text-xs h-8" onClick={() => openVotacao(v)} data-testid={`btn-responder-${v.votacao_id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> Responder
                        </Button>
                      )}
                      {v.minha_resposta && v.status !== 'encerrada' && (v.status === 'enviada' || v.status === 'respondida') && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600 hover:bg-amber-50" onClick={() => openEditResposta(v)} title="Editar resposta" data-testid={`editar-resposta-${v.votacao_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {canSeeResults(v) && (
                        <Button variant="outline" size="sm" className="text-blue-600 border-blue-600 hover:bg-blue-50 text-xs h-8" onClick={() => openResultados(v)} data-testid={`ver-resultado-${v.votacao_id}`}>
                          <BarChart3 className="w-3.5 h-3.5 mr-1" /> Resultados
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Vote className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{search || filter !== 'todas' ? 'Nenhuma votação encontrada' : 'Nenhuma votação disponível'}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Respond / Edit Dialog */}
      <Dialog open={!!selectedVotacao} onOpenChange={() => { setSelectedVotacao(null); setEditingResposta(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingResposta ? 'Editar Resposta' : 'Responder Votação'}</DialogTitle>
          </DialogHeader>
          {selectedVotacao && (
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-xl p-4">
                <p className="font-semibold text-sm text-slate-900 mb-4">{selectedVotacao.pergunta}</p>
                {selectedVotacao.mostrar_descricao && selectedVotacao.descricao && (
                  <p className="text-xs text-slate-500 mb-4">{selectedVotacao.descricao}</p>
                )}

              {selectedVotacao.tipo === 'texto' ? (
                <Textarea value={respostaTexto} onChange={e => setRespostaTexto(e.target.value)} className="min-h-[80px] bg-white border-slate-200" data-testid="input-resposta-texto" />
              ) : (
                <div>
                  {(selectedVotacao.opcoes || []).map((op, i) => {                    const selected = opcoesSelecionadas.includes(i);
                    return (
                      <button key={i} onClick={() => toggleOpcao(i, selectedVotacao.permitir_multiplas)}
                        className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-center gap-3 mt-2.5 first:mt-0 ${selected ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`opcao-resposta-${i}`}>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'}`}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm text-slate-900">{op.texto}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setSelectedVotacao(null)}>Cancelar</Button>
                <Button onClick={handleResponder} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-enviar-resposta">
                  {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Aguarde...</> : <><Send className="w-4 h-4 mr-1" /> {editingResposta ? 'Salvar alteração' : 'Enviar resposta'}</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!resultadoOpen} onOpenChange={() => { setResultadoOpen(null); setResultadoData(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resultados</DialogTitle>
          </DialogHeader>
          {resultadoOpen && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-slate-900 mb-4">{resultadoOpen.pergunta}</p>

              {!resultadoData && <p className="text-sm text-slate-400 text-center py-4">A carregar...</p>}

              {resultadoData && resultadoOpen.tipo === 'multipla_escolha' && (
                <div className="space-y-4">
                  {resultadoData.votacao.opcoes?.map((op, i) => {
                    const count = resultadoData.opcoes_contagem[i] || 0;
                    const total = resultadoData.total || 1;
                    const pct = Math.round((count / total) * 100);
                    const isCorreta = resultadoData.votacao.marcar_correta && op.correta;
                    return (
                      <div key={i} className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs text-slate-700 font-medium">{op.texto}</p>
                            {isCorreta && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-1.5 py-0.5 rounded">Correta</span>}
                          </div>
                          <span className="text-xs text-slate-500">{count} / {pct}%</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${isCorreta ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {resultadoData && resultadoOpen.tipo === 'texto' && (
                <div className="space-y-2">
                  {resultadoData.respostas.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                          <span className="text-[10px] text-slate-400">{fmtDate(r.updated_at || r.timestamp)}</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">"{r.texto}"</p>
                      </div>
                    </div>
                  ))}
                  {resultadoData.respostas.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">Nenhuma resposta ainda.</p>
                  )}
                </div>
              )}

              {/* Lista de quem respondeu (múltipla escolha) */}
              {resultadoData && resultadoOpen.tipo === 'multipla_escolha' && resultadoData.respostas.length > 0 && (
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-700 mb-2">Respostas individuais</p>
                  <div className="space-y-2">
                    {resultadoData.respostas.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{r.user_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-800">{r.user_name}</p>
                            <span className="text-[10px] text-slate-400">{fmtDate(r.updated_at || r.timestamp)}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">
                            "{(r.opcoes_selecionadas || []).map(idx => resultadoData.votacao.opcoes?.[idx]?.texto).filter(Boolean).join(', ')}"
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resultadoData && (
                <p className="text-xs text-slate-500 pt-1 border-t border-slate-100">{resultadoData.total} {resultadoData.total === 1 ? 'resposta' : 'respostas'} no total</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
